import React from 'react';
import { LuUsers, LuBed, LuMaximize, LuLeaf, LuClock, LuBan, LuCigarette } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';

const mockRooms = [
  { _id: '1', slug: 'chembra-view-suite', name: 'Chembra Peak Suite', type: 'suite', pricePerNight: 6500, capacity: 2, bedType: 'King Bed', size: 420, shortDesc: 'Wake up to the majestic Chembra Peak from your luxury balcony.', view: 'Mountain', amenities: ['Mountain View','Private Balcony','AC','WiFi','En-suite Bath','Mini Fridge'], images: [{ url: '/room1.jpeg' }], isFeatured: true },
  { _id: '2', slug: 'tea-garden-deluxe', name: 'Tea Garden Premium', type: 'deluxe', pricePerNight: 4800, capacity: 2, bedType: 'Queen Bed', size: 320, shortDesc: 'Surrounded by lush tea estates with forest stream sounds.', view: 'Garden', amenities: ['Garden View','AC','WiFi','En-suite Bath','Sit-out','Mini Fridge'], images: [{ url: '/room2.jpeg' }], isFeatured: true },
  { _id: '3', slug: 'valley-mist-cottage', name: 'Valley Mist Retreat', type: 'cottage', pricePerNight: 5500, capacity: 4, bedType: 'Two Beds', size: 380, shortDesc: 'A secluded cottage in the mist, perfect for families.', view: 'Valley', amenities: ['Valley View','Private Sit-out','AC','WiFi','Family Suite','Mini Fridge'], images: [{ url: '/room3.jpeg' }], isFeatured: true },
  { _id: '4', slug: 'forest-stream-premium', name: 'Forest Stream Executive', type: 'premium', pricePerNight: 5800, capacity: 2, bedType: 'King Bed', size: 360, shortDesc: 'Fall asleep to the melody of the mountain stream below.', view: 'Forest', amenities: ['Stream View','Balcony','AC','WiFi','Bathtub','Mini Fridge'], images: [{ url: '/room4.jpeg' }] },
];

export default function Rooms() {
  const { data: rooms, isLoading } = useQuery({
    queryKey: ['all-rooms'],
    queryFn: () => axios.get('/api/rooms').then(r => r.data).catch(() => mockRooms)
  });

  const displayRooms = rooms || mockRooms;

  return (
    <PageWrapper>
      <Helmet>
        <title>Luxury Rooms & Suites – Villa Tesori | Wayanad Mountain View Stay</title>
        <meta name="description" content="Browse our luxury suites and cottage rooms at Villa Tesori Wayanad. Features private balconies with 360° views of Chembra Peak and tea plantations." />
        <meta name="keywords" content="Wayanad room booking, Chembra peak view rooms, tea garden stay Wayanad, luxury cottage Wayanad, best rooms in Wayanad homestay, luxury stay Wayanad" />
        <meta property="og:title" content="Rooms & Suites – Villa Tesori Wayanad" />
        <meta property="og:description" content="Wake up to misty mountains. Explore our premium rooms and suites in the heart of Vellarimala." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/rooms" />
        <link rel="canonical" href="https://villatesori.com/rooms" />
      </Helmet>

      <PageHero
        scriptText="Rest & Rejuvenate"
        title="Rooms & Suites"
        subtitle="Each room is a world of its own — views that heal, comforts that restore"
        bgImage="/img7.jpeg"
      />

      <section className="section">
        <div className="container">
          {isLoading ? (
            <div className="flex-center" style={{ padding: 80 }}><div className="loading-spinner" /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
              {displayRooms.map((room, i) => (
                <div key={room._id} className="rooms-card" style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 60,
                  alignItems: 'center',
                }}>
                  {/* Image */}
                  <div className="img-overlay" style={{
                    height: 440, borderRadius: 2,
                    order: i % 2 === 0 ? 1 : 2,
                  }}>
                    <img src={room.images?.[0]?.url} alt={room.name} />
                    <div style={{ position: 'absolute', top: 20, left: 20 }}>
                      <span className="badge badge-gold" style={{ textTransform: 'capitalize' }}>{room.type}</span>
                    </div>
                    {room.isFeatured && (
                      <div style={{ position: 'absolute', top: 20, right: 20 }}>
                        <span className="badge badge-forest">Featured</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="rooms-card-content" style={{ order: i % 2 === 0 ? 2 : 1, padding: '0 12px' }}>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                      {room.view && <span style={{ fontSize: '0.75rem', color: 'var(--gold)', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Jost, sans-serif', display: 'flex', alignItems: 'center', gap: 6 }}><LuLeaf size={14} /> {room.view} View</span>}
                      {room.bedType && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Jost, sans-serif', display: 'flex', alignItems: 'center', gap: 6 }}><LuBed size={14} /> {room.bedType}</span>}
                    </div>
                    <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem, 4vw, 2.4rem)', color: 'var(--forest)', marginBottom: 12 }}>{room.name}</h2>
                    <p style={{ color: 'var(--text-muted)', lineHeight: 1.9, marginBottom: 24 }}>{room.shortDesc}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
                      {room.amenities?.map(a => (
                        <span key={a} className="badge badge-sage" style={{ fontSize: '0.72rem' }}>{a}</span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0', borderTop: '1px solid var(--mist)', borderBottom: '1px solid var(--mist)', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', marginBottom: 4 }}>Starting from</div>
                        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem, 4vw, 2.2rem)', color: 'var(--forest)' }}>
                          ₹{room.pricePerNight?.toLocaleString()}<span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontFamily: 'Jost, sans-serif', fontWeight: 300 }}>/night</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}><LuUsers size={14} /> {room.capacity} guests</div>
                        {room.size && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}><LuMaximize size={14} /> {room.size} sq ft</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <Link to={`/rooms/${room.slug}`} className="btn btn-primary">View Details</Link>
                      <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ textDecoration: 'none' }}>Enquire Now</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Policies */}
      <section className="section-sm" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div className="policies-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32 }}>
            {[
              { icon: <LuClock />, title: 'Check-in', text: 'From 2:00 PM' },
              { icon: <LuClock />, title: 'Check-out', text: 'Until 11:00 AM' },
              { icon: <LuBan />, title: 'Pets', text: 'Not allowed' },
              { icon: <LuCigarette />, title: 'Smoking', text: 'Outside only' },
            ].map(p => (
              <div key={p.title} style={{ textAlign: 'center', padding: '24px 16px' }}>
                <span style={{ fontSize: '1.8rem', marginBottom: 12, display: 'block', color: 'var(--gold)' }}>{p.icon}</span>
                <h4 style={{ fontSize: '1rem', marginBottom: 4 }}>{p.title}</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
